import {UserListFilter, UserListOrderBy, UserListPagination} from '~/graphql-react-query/graphql';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {useMutation, useQuery} from '@tanstack/react-query';
import {
    ReportChangeOwnership,
    SegmentChangeOwnership,
    UserItem,
    UserList,
    UserUpdateSessionMetadata
} from './graphqlQueries';
import {TeamMember, User} from 'bigdatr-style';

type UserListPayload = {
    filter: UserListFilter;
    orderBy: UserListOrderBy;
    pagination: UserListPagination;
};

type UserSessionPayload = {
    id: string;
    sessionMetadata?: Record<string, any> | null;
};

type UserItemPayload = {
    id: string;
};

type ChangeOwnershipPayload = {
    newUserId: string;
    teamId: string;
    reportIds: string[];
    segmentIds: string[];
};

export function useUserList(payload: UserListPayload) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: ['userList', payload.filter, payload.orderBy, payload.pagination],
        queryFn: async () => {
            const response = await request(UserList, {
                filter: payload.filter,
                orderBy: payload.orderBy,
                pagination: payload.pagination
            });

            if (!response?.user?.userList) {
                throw new Error('User list not returned from API');
            }

            const users = response.user.userList.map(mapGraphQLUserToUserClass);
            return users;
        }
    });
}

export function useUserItem(payload: UserItemPayload) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: ['userItem', payload.id],
        queryFn: async () => {
            const response = await request(UserItem, {
                id: payload.id
            });

            if (!response?.user?.user) {
                throw new Error('User item not returned from API');
            }

            const user = mapGraphQLUserToUserClass(response.user.user);

            return user;
        }
    });
}

export function useUserUpdateSession() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async (input: UserSessionPayload) => {
            const response = await request(UserUpdateSessionMetadata, {
                user: {
                    id: input.id,
                    sessionMetadata: input.sessionMetadata
                }
            });
            return response.user;
        }
    });
}

export function useUserChangeOwnership() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (input: ChangeOwnershipPayload) => {
            const {reportIds, segmentIds, newUserId, teamId} = input;

            const reportRequests = reportIds.map((id) =>
                request(ReportChangeOwnership, {
                    input: {id, newUserId, newTeamId: teamId}
                })
            );

            const segmentRequests = segmentIds.map((id) =>
                request(SegmentChangeOwnership, {
                    input: {id, newUserId, newTeamId: teamId}
                })
            );

            // Run both in parallel
            const [reportResults, segmentResults] = await Promise.all([
                Promise.all(reportRequests),
                Promise.all(segmentRequests)
            ]);

            return {
                reports: reportResults.map(
                    (response) => response.reportV2?.reportChangeOwnership?.report
                ),
                segments: segmentResults.map(
                    (response) => response?.segmentV2?.segmentChangeOwnership
                )
            };
        }
    });
}

//helper
export function mapGraphQLUserToUserClass(raw: any): User {
    return new User({
        id: raw.id,
        username: raw.username,
        name: raw.name,
        createdAt: raw.createdAt ?? undefined,
        updatedAt: raw.updatedAt ?? undefined,
        status: {
            australianMediaValue: raw.status?.australianMediaValue,
            australianCreative: raw.status?.australianCreative,
            newZealandCreative: raw.status?.newZealandCreative,
            adminProducts: raw.status?.adminProducts ?? []
        },
        memberships: (raw.memberships ?? []).map(
            (m: any) =>
                new TeamMember({
                    teamId: m.teamId,
                    team: m.team ?? undefined
                })
        )
    });
}
